
import { FormData } from "@/types/inventory";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  validateItemForm,
  saveItemOffline,
  saveItemToDatabase,
  checkForDuplicateItems
} from "./utils";

export const useAddItemOperations = (state: {
  formData: FormData;
  errors: Record<string, string>;
  setErrors: (errors: Record<string, string>) => void;
  setIsProcessing: (isProcessing: boolean) => void;
  setIsDuplicate: (isDuplicate: boolean) => void;
  isOffline: boolean;
  pendingItems: FormData[];
  onOpenChange: (open: boolean) => void;
  onItemAdded?: () => void;
  onAddItem?: (item: any) => void;
}) => {
  const { user } = useAuth();
  const {
    formData,
    errors,
    setErrors,
    setIsProcessing,
    setIsDuplicate,
    isOffline,
    pendingItems,
    onOpenChange,
    onItemAdded,
    onAddItem
  } = state;

  const validateForm = () => {
    const newErrors = validateItemForm(formData);
    setErrors(newErrors);
    
    return Object.values(newErrors).every((error) => error === "");
  };

  const saveItem = async (item: FormData) => {
    if (isOffline) {
      saveItemOffline(item);
      return;
    }
    
    try {
      const savedItem = await saveItemToDatabase(item, user?.id);
      
      if (onAddItem && savedItem) {
        onAddItem(savedItem);
      }
      
      toast.success("Item added successfully!", {
        position: "bottom-right",
        duration: 3000,
      });
    } catch (error) {
      console.error("Error saving item:", error);
      throw error;
    }
  };

  const saveMultipleItems = async () => {
    for (const item of pendingItems) {
      try {
        await saveItem(item);
      } catch (error) {
        console.error(`Error saving item ${item.name}:`, error);
      }
    }
    
    toast.success(`${pendingItems.length} items added to inventory!`, {
      position: "bottom-right",
      duration: 3000,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsProcessing(true);
    
    try {
      const isDuplicate = await checkForDuplicateItems(formData, user?.id);
      
      if (isDuplicate) {
        setIsDuplicate(true);
        setIsProcessing(false);
        return;
      }
      
      if (pendingItems.length > 0) {
        await saveMultipleItems();
      } else {
        await saveItem(formData);
      }
      
      if (onItemAdded) {
        onItemAdded();
      }
      
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error adding item:", error);
      toast.error(`Failed to add item: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleForceSave = async () => {
    setIsDuplicate(false);
    setIsProcessing(true);
    
    try {
      await saveItem(formData);
      
      if (onItemAdded) {
        onItemAdded();
      }
      
      onOpenChange(false);
    } catch (error: any) {
      console.error("Error adding item:", error);
      toast.error(`Failed to add item: ${error.message}`);
    } finally {
      setIsProcessing(false);
    }
  };

  return {
    validateForm,
    handleSubmit,
    handleForceSave
  };
};
